/**
 * @license
 * Copyright Google Inc. All Rights Reserved.
 *
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://angular.io/license
 */


import {Inject, Injectable, Optional} from '@angular/core';
import {DateAdapter, MAT_DATE_LOCALE} from './date-adapter';
import {extendObject} from '../util/object-extend';
/**
 * Whether the browser supports the Intl API.
 */
const SUPPORTS_INTL_API = typeof Intl != 'undefined';
/**
 * The default month names to use if Intl API is not available.
 */
const DEFAULT_MONTH_NAMES = {
  'long': [
    'January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September',
    'October', 'November', 'December'
  ],
  'short': ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
  'narrow': ['J', 'F', 'M', 'A', 'M', 'J', 'J', 'A', 'S', 'O', 'N', 'D']
};
/**
 * The default date names to use if Intl API is not available.
 */
const DEFAULT_DATE_NAMES = range(31, i => String(i + 1));
/**
 * The default day of the week names to use if Intl API is not available.
 */
const DEFAULT_DAY_OF_WEEK_NAMES = {
  'long': ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
  'short': ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'],
  'narrow': ['S', 'M', 'T', 'W', 'T', 'F', 'S']
};
/**
 * Matches strings that have the form of a valid RFC 3339 string
 * (https://tools.ietf.org/html/rfc3339). Note that the string may not actually be a valid date
 * because the regex will match strings an with out of bounds month, date, etc.
 */
const ISO_8601_REGEX =
    /^\d{4}-\d{2}-\d{2}(?:T\d{2}:\d{2}:\d{2}(?:\.\d+)?(?:Z|(?:(?:\+|-)\d{2}:\d{2}))?)?$/;
/**
 * Creates an array and fills it with values.
 * @template T
 * @param {?} length
 * @param {?} valueFunction
 * @return {?}
 */
function range<T>(length: number, valueFunction: (index: number) => T): T[] {
  const /** @type {?} */ valuesArray = Array(length);
  for (let /** @type {?} */ i = 0; i < length; i++) {
    valuesArray[i] = valueFunction(i);
  }
  return valuesArray;
}
/**
 * Adapts the native JS Date for use with cdk-based components that work with dates.
 */
export class NativeDateAdapter extends DateAdapter<Date> {
/**
 * @param {?} matDateLocale
 */
constructor(  matDateLocale: string) {
    super();
    super.setLocale(matDateLocale);
  }
/**
 * Whether to use `timeZone: 'utc'` with `Intl.DateTimeFormat` when formatting dates.
 * Without this `Intl.DateTimeFormat` sometimes chooses the wrong timeZone, which can throw off
 * the result. (e.g. in the en-US locale `new Date(1800, 7, 14).toLocaleDateString()`
 * will produce `'8/13/1800'`.
 */
useUtcForDisplay = true;
/**
 * @param {?} date
 * @return {?}
 */
getYear(date: Date): number {
    return date.getFullYear();
  }
/**
 * @param {?} date
 * @return {?}
 */
getMonth(date: Date): number {
    return date.getMonth();
  }
/**
 * @param {?} date
 * @return {?}
 */
getDate(date: Date): number {
    return date.getDate();
  }
/**
 * @param {?} date
 * @return {?}
 */
getDayOfWeek(date: Date): number {
    return date.getDay();
  }
/**
 * @param {?} style
 * @return {?}
 */
getMonthNames(style: 'long' | 'short' | 'narrow'): string[] {
    if (SUPPORTS_INTL_API) {
      let /** @type {?} */ dtf = new Intl.DateTimeFormat(this.locale, {month: style});
      return range(12, i => this._stripDirectionalityCharacters(dtf.format(new Date(2017, i, 1))));
    }
    return DEFAULT_MONTH_NAMES[style];
  }
/**
 * @return {?}
 */
getDateNames(): string[] {
    if (SUPPORTS_INTL_API) {
      let /** @type {?} */ dtf = new Intl.DateTimeFormat(this.locale, {day: 'numeric'});
      return range(31, i => this._stripDirectionalityCharacters(
          dtf.format(new Date(2017, 0, i + 1))));
    }
    return DEFAULT_DATE_NAMES;
  }
/**
 * @param {?} style
 * @return {?}
 */
getDayOfWeekNames(style: 'long' | 'short' | 'narrow'): string[] {
    if (SUPPORTS_INTL_API) {
      let /** @type {?} */ dtf = new Intl.DateTimeFormat(this.locale, {weekday: style});
      return range(7, i => this._stripDirectionalityCharacters(
          dtf.format(new Date(2017, 0, i + 1))));
    }
    return DEFAULT_DAY_OF_WEEK_NAMES[style];
  }
/**
 * @param {?} date
 * @return {?}
 */
getYearName(date: Date): string {
    if (SUPPORTS_INTL_API) {
      let /** @type {?} */ dtf = new Intl.DateTimeFormat(this.locale, {year: 'numeric'});
      return this._stripDirectionalityCharacters(dtf.format(date));
    }
    return String(this.getYear(date));
  }
/**
 * @return {?}
 */
getFirstDayOfWeek(): number {
    // We can't tell using native JS Date what the first day of the week is, we default to Sunday.
    return 0;
  }
/**
 * @param {?} date
 * @return {?}
 */
getNumDaysInMonth(date: Date): number {
    return this.getDate(this._createDateWithOverflow(
        this.getYear(date), this.getMonth(date) + 1, 0));
  }
/**
 * @param {?} date
 * @return {?}
 */
clone(date: Date): Date {
    return this.createDate(this.getYear(date), this.getMonth(date), this.getDate(date));
  }
/**
 * @param {?} year
 * @param {?} month
 * @param {?} date
 * @return {?}
 */
createDate(year: number, month: number, date: number): Date {
    // Check for invalid month and date (except upper bound on date which we have to check after
    // creating the Date).
    if (month < 0 || month > 11) {
      throw Error(`Invalid month index "${month}". Month index has to be between 0 and 11.`);
    }

    if (date < 1) {
      throw Error(`Invalid date "${date}". Date has to be greater than 0.`);
    }

    let /** @type {?} */ result = this._createDateWithOverflow(year, month, date);

    // Check that the date wasn't above the upper bound for the month, causing the month to overflow
    if (result.getMonth() != month) {
      throw Error(`Invalid date "${date}" for month with index "${month}".`);
    }

    return result;
  }
/**
 * @return {?}
 */
today(): Date {
    return new Date();
  }
/**
 * @param {?} value
 * @return {?}
 */
parse(value: any): Date | null {
    // We have no way using the native JS Date to set the parse format or locale, so we ignore these
    // parameters.
    if (typeof value == 'number') {
      return new Date(value);
    }
    return value ? new Date(Date.parse(value)) : null;
  }
/**
 * @param {?} date
 * @param {?} displayFormat
 * @return {?}
 */
format(date: Date, displayFormat: Object): string {
    if (!this.isValid(date)) {
      throw Error('NativeDateAdapter: Cannot format invalid date.');
    }
    if (SUPPORTS_INTL_API) {
      if (this.useUtcForDisplay) {
        date = new Date(Date.UTC(
            date.getFullYear(), date.getMonth(), date.getDate(), date.getHours(),
            date.getMinutes(), date.getSeconds(), date.getMilliseconds()));
        displayFormat = extendObject({}, displayFormat, {timeZone: 'utc'});
      }
      let /** @type {?} */ dtf = new Intl.DateTimeFormat(this.locale, displayFormat);
      return this._stripDirectionalityCharacters(dtf.format(date));
    }
    return this._stripDirectionalityCharacters(date.toDateString());
  }
/**
 * @param {?} date
 * @param {?} years
 * @return {?}
 */
addCalendarYears(date: Date, years: number): Date {
    return this.addCalendarMonths(date, years * 12);
  }
/**
 * @param {?} date
 * @param {?} months
 * @return {?}
 */
addCalendarMonths(date: Date, months: number): Date {
    let /** @type {?} */ newDate = this._createDateWithOverflow(
        this.getYear(date), this.getMonth(date) + months, this.getDate(date));

    // It's possible to wind up in the wrong month if the original month has more days than the new
    // month. In this case we want to go to the last day of the desired month.
    // Note: the additional + 12 % 12 ensures we end up with a positive number, since JS % doesn't
    // guarantee this.
    if (this.getMonth(newDate) != ((this.getMonth(date) + months) % 12 + 12) % 12) {
      newDate = this._createDateWithOverflow(this.getYear(newDate), this.getMonth(newDate), 0);
    }

    return newDate;
  }
/**
 * @param {?} date
 * @param {?} days
 * @return {?}
 */
addCalendarDays(date: Date, days: number): Date {
    return this._createDateWithOverflow(
        this.getYear(date), this.getMonth(date), this.getDate(date) + days);
  }
/**
 * @param {?} date
 * @return {?}
 */
toIso8601(date: Date): string {
    return [
      date.getUTCFullYear(),
      this._2digit(date.getUTCMonth() + 1),
      this._2digit(date.getUTCDate())
    ].join('-');
  }
/**
 * @param {?} iso8601String
 * @return {?}
 */
fromIso8601(iso8601String: string): Date | null {
    // The `Date` constructor accepts formats other than ISO 8601, so we need to make sure the
    // string is the right format first.
    if (ISO_8601_REGEX.test(iso8601String)) {
      let /** @type {?} */ d = new Date(iso8601String);
      if (this.isValid(d)) {
        return d;
      }
    }
    return null;
  }
/**
 * @param {?} obj
 * @return {?}
 */
isDateInstance(obj: any) {
    return obj instanceof Date;
  }
/**
 * @param {?} date
 * @return {?}
 */
isValid(date: Date) {
    return !isNaN(date.getTime());
  }
/**
 * Creates a date but allows the month and date to overflow.
 * @param {?} year
 * @param {?} month
 * @param {?} date
 * @return {?}
 */
private _createDateWithOverflow(year: number, month: number, date: number) {
    let /** @type {?} */ result = new Date(year, month, date);

    // We need to correct for the fact that JS native Date treats years in range [0, 99] as
    // abbreviations for 19xx.
    if (year >= 0 && year < 100) {
      result.setFullYear(this.getYear(result) - 1900);
    }
    return result;
  }
/**
 * Pads a number to make it two digits.
 * @param {?} n The number to pad.
 * @return {?} The padded number.
 */
private _2digit(n: number) {
    return ('00' + n).slice(-2);
  }
/**
 * Strip out unicode LTR and RTL characters. Edge and IE insert these into formatted dates while
 * other browsers do not. We remove them to make output consistent and because they interfere with
 * date parsing.
 * @param {?} str The string to strip direction characters from.
 * @return {?} The stripped string.
 */
private _stripDirectionalityCharacters(str: string) {
    return str.replace(/[\u200e\u200f]/g, '');
  }
static decorators: DecoratorInvocation[] = [
{ type: Injectable },
];
/**
 * @nocollapse
 */
static ctorParameters: () => ({type: any, decorators?: DecoratorInvocation[]}|null)[] = () => [
{type: undefined, decorators: [{ type: Optional }, { type: Inject, args: [MAT_DATE_LOCALE, ] }, ]},
];
}

function NativeDateAdapter_tsickle_Closure_declarations() {
/** @type {?} */
NativeDateAdapter.decorators;
/**
 * @nocollapse
 * @type {?}
 */
NativeDateAdapter.ctorParameters;
/**
 * Whether to use `timeZone: 'utc'` with `Intl.DateTimeFormat` when formatting dates.
 * Without this `Intl.DateTimeFormat` sometimes chooses the wrong timeZone, which can throw off
 * the result. (e.g. in the en-US locale `new Date(1800, 7, 14).toLocaleDateString()`
 * will produce `'8/13/1800'`.
 * @type {?}
 */
NativeDateAdapter.prototype.useUtcForDisplay;
}


interface DecoratorInvocation {
  type: Function;
  args?: any[];
}
